<template lang="pug">
div(v-if='canShow && installationQuote' class='flex items-center gap-x-4')
  div(v-if='showIcon')
    Wrench(class='w-8 h-auto')

  div(class='relative flex-1')
    div(class='flex flex-wrap items-center')
      span(class='font-bold') Add Professional Installation

      UniversalLink(
        v-if='$sitewideConfig.config.installernetPageUrl'
        :link='$sitewideConfig.config.installernetPageUrl'
        open-new-window
      )
        InfoIcon(class='w-4 h-4 cursor-pointer text-action fill-current align-middle ml-1 focus:outline-none')

    div(class='flex flex-wrap gap-x-1')
      span(class='font-bold') RealPro
      span(class='whitespace-nowrap') Available in
      button(class='underline cursor-pointer whitespace-nowrap' type='button' @click='openLocationModal') {{ formattedInstallLocation }}

      SwitchGroup
        div(class='flex items-center gap-x-2.5')
          Switch(
            v-slot='{ checked }'
            as='template'
            type='button'
            :model-value='hasInstallation'
            @update:model-value='toggleInstallation'
          )
            button(
              class='relative inline-flex justify-center items-center h-5 w-5 border-2 border-solid rounded focus:outline-none focus:ring-2 focus:ring-offset-2'
              :class='checked ? "bg-action border-action" : "border-gray-dark"'
            )
              img(v-if='checked' src='/images/checkmarkThick.svg' class='w-3 h-3' height='12' width='12')

          SwitchLabel(class='flex-1')
            span
              span Include Installation:
              span(class='inline-flex items-center ml-1 font-bold space-x-2')
                span(class='text-base') {{ formatCents(installationQuote.sellPrice) }}
                s(v-if='installationQuote.sellPrice !== installationQuote.regularPrice' class='text-gray text-sm') {{ formatCents(installationQuote.regularPrice) }}

    div(v-if='errorText' class='text-danger text-xs')
      | {{ errorText }}

    div(v-if='isWaiting' class='bg-black opacity-50 absolute inset-0 flex justify-center items-center')
      div(class='loader !border-[7px] !w-12 !h-12')

GooglePlacesLocationModal(
  :modal-unique-key='locationModalId'
  :location-override='formattedInstallLocation'
  @location-changed='updateInstallLocation'
)
</template>

<script setup lang="ts">
import InfoIcon from '@/assets/info.svg?component'
import Wrench from '@/integrations/installernet/assets/wrench.svg?component'
import { SwitchGroup, SwitchLabel, Switch, provideUseId } from '@headlessui/vue'

// We need to do this to so headlessUI doesn't cause hydration errors/
// See: https://github.com/tailwindlabs/headlessui/issues/2913#issuecomment-1924681165
provideUseId(() => useId())

const { item, showIcon } = defineProps<{
  item: CartItem | ProductLine | GroupProduct
  showIcon?: boolean
}>()

const { $installernet, $uiEvents, $ketch, $overlay } = useNuxtApp()
const { isIntegrationEnabled } = useUtils()
const { getApiUrl } = useUrls()
const fitmentStore = useFitmentStore()
const cart = useCart()
const notifications = useNotifications()
const location = useLocation()

const quotesUrl = getApiUrl('installation') + 'quotes'
const locationModalId = `installLocationModal-${useId()}`

const isWaiting = ref(false)
const installationQuote = ref<Installation>()
const errorText = ref('')

const canShow = computed(() => {
  return !isIntegrationEnabled('ketch') || $ketch.hasConsent.value
})

const hasInstallation = computed(() => {
  return isCartItem(item) ? !!item.installation : !!$installernet.getQuote(item.sku)
})

const installLocation = computed(() => {
  const loc = {
    city: location.state.value.city,
    state: location.state.value.region,
    zip: location.state.value.zipcode,
  }

  if (isCartItem(item) && item.installationAvailable) {
    loc.city = item.installationAvailable.city
    loc.state = item.installationAvailable.state
    loc.zip = item.installationAvailable.zip
  }

  return loc
})

const formattedInstallLocation = computed(() => {
  if (!installLocation.value.city || !installLocation.value.state || !installLocation.value.zip)
    return 'Unknown location'

  return `${installLocation.value.city}, ${installLocation.value.state} ${installLocation.value.zip}`
})

onMounted(() => {
  if (isCartItem(item)) installationQuote.value = item.installation
  else getQuote(installLocation.value.zip)
})

onUnmounted(() => {
  // cleanup this sku
  $installernet.setQuote(item.sku)
})

watch(
  () => location.state.value.zipcode,
  async (newZip) => {
    // If we have an CartItem and it has an installationAvailable then we can early out
    // because we don't care about the global geolocation and only the location on
    // install of the item
    if (isCartItem(item) && !item.installationAvailable) return

    $installernet.clearQuotes()

    await getQuote(newZip)
  }
)

function isCartItem(item: CartItem | ProductLine | GroupProduct): item is CartItem {
  return 'lineId' in item
}

function openLocationModal() {
  const modalId = isCartItem(item) && item.installationAvailable ? locationModalId : 'locationModal'
  $overlay.open(modalId)
}

async function toggleInstallation(val: boolean) {
  errorText.value = ''

  if (isCartItem(item)) {
    isWaiting.value = true

    // Adding install
    if (val) {
      // We need a valid quote, city, and state. Early out if not.
      if (!installationQuote.value?.quoteId || !installLocation.value.city || !installLocation.value.state) {
        errorText.value = 'Unable to add, please try again.'
        isWaiting.value = false
        return
      }

      try {
        await cart.addItemInstallation(item.id, {
          quoteId: installationQuote.value.quoteId,
          city: installLocation.value.city,
          state: installLocation.value.state,
        })
        $uiEvents.$emit('installationAdded', item)
      } catch (error) {
        errorText.value = 'Unable to add, please try again.'
      }
    } else {
      try {
        await cart.removeItemInstallation(item.id)
        $uiEvents.$emit('installationRemoved', item)
      } catch (error) {
        errorText.value = 'Unable to remove, please try again.'
      }

      // When we remove we need to make sure the that the quote is still valid
      if (installationQuote.value) {
        const now = new Date()
        const installExpirationDate = new Date(installationQuote.value.expirationDate)
        // Our expiration threshold is 1 day sooner than the actual expiration of the quote
        installExpirationDate.setDate(installExpirationDate.getDate() - 1)

        if (now >= installExpirationDate) {
          getQuote(installLocation.value.zip)
        }
      } else {
        getQuote(installLocation.value.zip)
      }
    }

    isWaiting.value = false

    return
  }

  // Store the sku:quote so we can add it to cart later
  if (val) $installernet.setQuote(item.sku, installationQuote.value?.quoteId)
  // Remove the quote by setting the sku to nothing
  else $installernet.setQuote(item.sku)
}

async function updateInstallLocation(location: UserLocation) {
  if (!isCartItem(item)) return

  try {
    isWaiting.value = true

    // Update the Quote for this item
    await cart.updateItemInstallation(item.id, {
      city: location.city,
      state: location.region,
      zip: location.zipcode,
    })

    // Refresh the quote we wish to offer them for the new zip
    // getQuote will handle the this.isWaiting from here
    getQuote(location.zipcode)
  } catch (error) {
    notifications.addError('Error', 'Unable to update installation. Please try again later.')
    isWaiting.value = false
  }
}

async function getQuote(zipcode: string) {
  try {
    isWaiting.value = true

    let fitmentDetails = {
      year: fitmentStore.year && +fitmentStore.year,
      make: fitmentStore.make,
      model: fitmentStore.model,
      bed: fitmentStore.bed,
      body: fitmentStore.body,
      engine: fitmentStore.engine,
    }

    if (isCartItem(item) && item.fitmentData) {
      fitmentDetails = {
        year: +item.fitmentData.year,
        make: item.fitmentData.makeName,
        model: item.fitmentData.modelName,
        bed: item.fitmentData.bedName,
        body: item.fitmentData.bodyName,
        engine: item.fitmentData.engineName,
      }
    }

    const { data } = await $fetch<{ data: Installation }>(quotesUrl, {
      method: 'POST',
      body: {
        zip: zipcode,
        skuSlug: item.skuSlug,
        details: fitmentDetails,
      },
    })

    installationQuote.value = data

    // Only fire this event when NOT in checkout mode
    if (!isCartItem(item)) $uiEvents.$emit('installernetOffered', item)
  } catch (error) {
    installationQuote.value = undefined
  } finally {
    isWaiting.value = false
  }
}
</script>
